import { PRINTER_MESSAGE, PRINTER_STATUS, PRINTER_TYPE } from '../../api/core/printer/printer.constants';
import { interval, Subject, Subscription } from 'rxjs';
import { IPrinterStatus, IPrintResponse } from 'src/app/api/core/printer/printer.interface';

import { WatcherService } from 'src/app/api/utils/watcher/watcher.service';
import { PRINTER_STATUS_CHECK_COMPLETED, PRINTER_STATUS_PING } from 'src/app/api/utils/watcher/watcher-event';

import each from 'lodash/each';

import EscPosEncoder from 'esc-pos-encoder';
import { UtilityClass } from 'src/app/api/utils/UtilityClass';

declare var Socket;

export class Printer {
  /** Printer name */
  private _name: string;

  /** Printer MAC/IP address */
  private _address: string;

  /** Printer protocol - used by iOS */
  private _protocols: string[];

  /** Printer connection type */
  private _type: PRINTER_TYPE = PRINTER_TYPE.BT;

  /** Printer port for Ethernet printers  */
  private _port: number = 9100;

  /** Flagger for active state */
  private _active: boolean = false;

  /** Flagger for pass state - Pass printers print the full docket */
  private _pass: boolean = false;

  /** Counter on duplicate prints */
  private _repeat: number = 1;

  /** Socket used to communicate to Ethernet printers */
  public ethernetSocket: any;

  /** Output for activating the printer */
  public activateChanged: Subject<boolean> = new Subject();

  /** Output for changing the linked printer groups */
  public printerGroupsChanged: Subject<string> = new Subject();

  /** Printer status */
  private _printerStatus: IPrinterStatus = {
    success: true,
    status: PRINTER_STATUS.OK,
  };

  private _watcherService: WatcherService;
  private _lastPrinterChecked: number;

  public static PRINTER_PING_TIMER_SEC: number = 180; // 3 minutes
  private _pingTimerSub: Subscription;
  private _pingTimerRunningSecond: number = 0;

  public printerStatusChecked: Subject<IPrinterStatus> = new Subject<IPrinterStatus>();
  public printerStatusChecking: Subject<IPrinterStatus> = new Subject<IPrinterStatus>();

  constructor(printerObj: any) {
    this._name = printerObj.name;
    this._address = printerObj.address;
    this._protocols = printerObj.protocols;
    this._type = printerObj.type;
    this._port = +printerObj.port || 9100;
    this._repeat = +printerObj.repeat || 1;

    this.setPrinterActive(!!printerObj.active);
    this.Pass = !!printerObj.pass;
  }

  /**
   * Update the printer status from ping checks
   * @param status The printer status
   */
  updateStatus(status: IPrinterStatus) {
    this._printerStatus = status;

    if (status.status != PRINTER_STATUS.CHECKING) {
      this._lastPrinterChecked = new Date().valueOf();
      this.printerStatusChecked.next(status);

      if (this._watcherService) this._watcherService.broadcast(PRINTER_STATUS_CHECK_COMPLETED, this);
    } else {
      this.printerStatusChecking.next(status);
    }
  }

  /**
   * Retrieve the printer status from ping checks
   * @returns IPrinterStatus
   */
  getPrinterStatus(): IPrinterStatus {
    return this._printerStatus;
  }

  /**
   * Set the printer active state
   * @param active The status to set
   */
  setPrinterActive(active: boolean) {
    // only change if values are different
    // so the app will not emit a false activate event
    if (this._active != active) {
      this._active = active;
      this.activateChanged.next(this._active);
    }

    if (this._active) {
      this._restartPingTimer();
      this.restartSocket();
    } else {
      this.dispose();
    }
  }

  /**
   * Restart the socket connection and establish a new socket
   */
  public restartSocket() {
    // establish the ethernetSocket
    if (this._type == PRINTER_TYPE.IP && window['Socket']) {
      this._connectEtherPrinter();
    }
  }

  /**
   * Retrieve the timestamp of when the status was last chcked
   * @returns Timestamp
   */
  public getLastChecked(): number | null {
    return this._lastPrinterChecked;
  }

  /**
   * Attach the watcher service to the printer
   * @param watcher
   */
  public setWatcher(watcher: WatcherService) {
    this._watcherService = watcher;

    // if it is an active printer, start the timer
    if (this._active) {
      if (this._watcherService) this._watcherService.broadcast(PRINTER_STATUS_PING, this);
      this._restartPingTimer();
    }
  }

  dispose(): void {
    this.disconnectPrinter();
    if (this._pingTimerSub) this._pingTimerSub.unsubscribe();
  }

  private _restartPingTimer() {
    if (this._pingTimerSub) this._pingTimerSub.unsubscribe();
    this._pingTimerRunningSecond = Printer.PRINTER_PING_TIMER_SEC;

    this._pingTimerSub = interval(1000).subscribe(() => {
      this._pingTimerRunningSecond--;

      if (this._pingTimerRunningSecond == -1) {
        if (this.Active) {
          if (this._watcherService) this._watcherService.broadcast(PRINTER_STATUS_PING, this);

          // only if printer is active, try to restart
          this._restartPingTimer();
        } else {
          this._pingTimerSub.unsubscribe();
        }
      }
    });
  }

  export(): any {
    return {
      name: this._name,
      address: this._address,
      protocols: this._protocols,
      type: this._type,
      port: this._port,
      active: this._active,
      pass: this._pass,
      repeat: this._repeat,
    };
  }

  public get Printer_ping_time(): number {
    return this._pingTimerRunningSecond;
  }

  public set Name(val: string) {
    this._name = val;
  }
  public get Name(): string {
    return this._name;
  }

  public set Address(val: string) {
    this._address = val;
  }
  public get Address(): string {
    return this._address;
  }

  public get Protocols(): string[] {
    return this._protocols;
  }

  public get Type(): PRINTER_TYPE {
    return this._type;
  }

  public set Port(val: number) {
    this._port = val;
  }
  public get Port(): number {
    return this._port;
  }

  public get Active(): boolean {
    return this._active;
  }

  public set Pass(val: boolean) {
    this._pass = val;
  }
  public get Pass(): boolean {
    return this._pass;
  }

  public get Status(): IPrinterStatus {
    return this._printerStatus;
  }

  public set Print_repeat(val: number) {
    if (isNaN(val)) {
      this._repeat = 1;
    } else {
      this._repeat = Math.max(val, 1);
    }
  }
  public get Print_repeat(): number {
    if (isNaN(this._repeat)) {
      this._repeat = 1;
    }

    return this._repeat;
  }

  /**** STATIC FUNCTIONS ****/
  public static GetErrorMessageByStatus(errorMessage: string, alwaysIncludeErrorCode: boolean = true): string {
    let printErrorMessage: string = '';
    switch (errorMessage) {
      /** PRINTER MESSAGE **/
      case PRINTER_MESSAGE.PRINTER_NOT_ACTIVE:
        printErrorMessage =
          'You will need to activate the printer. The printer can be activated in the <strong>Printer Setup</strong>.';
        break;
      case PRINTER_MESSAGE.BLUETOOTH_DISABLED:
        printErrorMessage = "Please enable BLUETOOTH in the device's settings.";
        break;
      case PRINTER_MESSAGE.DEVICE_SNIFF_FAILED:
      case PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES:
        printErrorMessage =
          "You will need to pair your printer. Go to your device's BLUETOOTH settings and scan for your printer.";
        break;

      /** PRINTER STATUS **/
      case PRINTER_STATUS.COVER_OPEN:
        printErrorMessage = 'Please close the printer cover.';
        break;
      case PRINTER_STATUS.PAPER_OUT:
        printErrorMessage = 'Please make sure you have a roll of paper in the printer.';
        break;
      case PRINTER_STATUS.FEED_PRESSED:
        printErrorMessage = "Please check the feed button isn't in use while trying to print.";
        break;
      case PRINTER_STATUS.AUTOCUTTER_ERROR:
        printErrorMessage = 'Please check the paper roll is jammed.';
        break;

      // UNKNOWNS - GENERIC
      default:
        /**
         * PRINTER_STATUS
         *
         * > OFFLINE
         * > KICKOUT_LOW
         * > UNREACHABLE
         * > BUSY
         * > RECOVERING
         * > UNKNOWN
         * > SOCKET_CLOSED_#
         * > NOT_FOUND
         * > ERROR_OCCURRED
         *
         *
         *
         * PRINTER_MESSAGE
         *
         * > NO_PRINTER_SELECTED
         * > CONNECTION_FAILED
         * > CORDOVA_NOT_AVAILABLE
         * > DISCONNECTED
         */

        printErrorMessage += 'Please try to restart and reconnect your printer.';
        if (!alwaysIncludeErrorCode) {
          printErrorMessage += `<br><span class='error_reference'>Error Reference: ${errorMessage}</span>`;
        }
    }

    if (alwaysIncludeErrorCode) {
      printErrorMessage += `<br><span class='error_reference'>Error Reference: ${errorMessage}</span>`;
    }

    return printErrorMessage;
  }

  public static GetErrorSummary(errorMessage: string): string {
    let printErrorTitle: string = '';
    switch (errorMessage) {
      /** PRINTER MESSAGE **/
      case PRINTER_MESSAGE.PRINTER_NOT_ACTIVE:
        printErrorTitle = 'Printer not active.';
        break;
      case PRINTER_MESSAGE.BLUETOOTH_DISABLED:
        printErrorTitle = 'Bluetooth disabled.';
        break;
      case PRINTER_MESSAGE.DEVICE_SNIFF_FAILED:
      case PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES:
        printErrorTitle = 'Printer not paired.';
        break;

      /** PRINTER STATUS **/
      case PRINTER_STATUS.COVER_OPEN:
        printErrorTitle = 'Printer cover is open.';
        break;
      case PRINTER_STATUS.PAPER_OUT:
        printErrorTitle = 'Printer is out of paper.';
        break;
      case PRINTER_STATUS.FEED_PRESSED:
        printErrorTitle = 'Printer feed button is pressed.';
        break;
      case PRINTER_STATUS.AUTOCUTTER_ERROR:
        printErrorTitle = 'Paper is jammed.';
        break;

      // UNKNOWNS - GENERIC
      default:
        /**
         * PRINTER_STATUS
         *
         * > OFFLINE
         * > KICKOUT_LOW
         * > UNREACHABLE
         * > BUSY
         * > RECOVERING
         * > UNKNOWN
         * > SOCKET_CLOSED_#
         * > NOT_FOUND
         * > ERROR_OCCURRED
         *
         *
         *
         * PRINTER_MESSAGE
         *
         * > NO_PRINTER_SELECTED
         * > CONNECTION_FAILED
         * > CORDOVA_NOT_AVAILABLE
         * > DISCONNECTED
         */

        printErrorTitle = 'Printer is not available';
    }

    return printErrorTitle;
  }

  public static AnalysePrinterStatus(binarySet: number[], fn: number): IPrinterStatus {
    let retVal: IPrinterStatus = {
      success: true,
      status: PRINTER_STATUS.OK,
      statusFn: fn,
      binary: [...binarySet].reverse().join(''),
    };

    let defaultMap: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
    each(binarySet, (value, index) => {
      defaultMap[index] = value;
    });

    // drawer kick out (1) | cover (2)
    const index2: number = defaultMap[2];

    // online/offline (1) | paper feed (2)
    const index3: number = defaultMap[3];

    // waiting online recovery (1) | paper end (2)
    const index5: number = defaultMap[5];

    // paper feed pressed (1) | error occured (2)
    const index6: number = defaultMap[6];

    if (fn == 1) {
      if (!index2) {
        // Kick out connector pin 3 is LOW
        retVal.success = false;
        retVal.status = PRINTER_STATUS.KICKOUT_LOW;
      } else if (index3) {
        // Offline
        retVal.success = false;
        retVal.status = PRINTER_STATUS.OFFLINE;
      } else if (index5) {
        // Waiting for online recovery
        retVal.success = false;
        retVal.status = PRINTER_STATUS.RECOVERING;
      } else if (index6) {
        // Paper Feed pressed
        retVal.success = false;
        retVal.status = PRINTER_STATUS.FEED_PRESSED;
      }
    } else if (fn == 2) {
      if (index2) {
        // Cover is OPEN
        retVal.success = false;
        retVal.status = PRINTER_STATUS.COVER_OPEN;
      } else if (index3) {
        // Paper Feed pressed
        retVal.success = false;
        retVal.status = PRINTER_STATUS.FEED_PRESSED;
      } else if (index5) {
        // Paper End
        retVal.success = false;
        retVal.status = PRINTER_STATUS.PAPER_OUT;
      } else if (index6) {
        // Error occurred
        retVal.success = false;
        retVal.status = PRINTER_STATUS.ERROR_OCCURRED;
        // maybe call FN 3?
      }
    } else if (fn == 3) {
      // NOT USED UNTIL FURTHER NOTICE
      if (index2) {
        // Recoverable error occurred
        retVal.success = false;
        retVal.status = PRINTER_STATUS.RECOVERABLE_ERROR;
      } else if (index3) {
        // Auto cutter error
        retVal.success = false;
        retVal.status = PRINTER_STATUS.AUTOCUTTER_ERROR;
      } else if (index5) {
        // Unrecoverable error occurred
        retVal.success = false;
        retVal.status = PRINTER_STATUS.UNRECOVERABLE_ERROR;
      } else if (index6) {
        // Auto-recoverable error occurred
        retVal.success = false;
        retVal.status = PRINTER_STATUS.AUTORECOVERABLE_ERROR;
      }
    }

    return retVal;
  }

  /** ETHERNET ONLY - CHECKING STATUS **/
  public DLE_EOT_fn: number = 1;
  public DLE_EOT_waiting: boolean = false;

  public GS_a_timeout;
  public DLE_EOT_timeout;

  private _printerStatusReady: Subject<IPrinterStatus> = new Subject();
  async checkPrinterStatus(timeout: number = 5000): Promise<IPrinterStatus> {
    return new Promise(checkStatusResolver => {
      const printerStatusReadySub = this._printerStatusReady.subscribe(status => {
        clearTimeout(this.DLE_EOT_timeout);
        this._checkStatusReset();

        if (printerStatusReadySub) printerStatusReadySub.unsubscribe();
        checkStatusResolver(status);
      });

      this._sendStatusCheck(1);

      // assume the printer is unreachable if ping is longer than 15 seconds
      clearTimeout(this.DLE_EOT_timeout);
      this.DLE_EOT_timeout = setTimeout(() => {
        // ASSUME PRINTER UNREACHABLE
        this._printerStatusReady.next({
          success: false,
          status: PRINTER_STATUS.UNREACHABLE,
          statusFn: this.DLE_EOT_fn,
        });

        this._checkStatusReset();
      }, timeout);
    });
  }

  /**
   * Initialise and opens a new ethernet socket
   */
  private async _connectEtherPrinter() {
    return new Promise(resolve => {
      // make sure to kill off the active socket first
      this.disconnectPrinter();

      // initialise the socket
      this.ethernetSocket = new Socket();

      // these are required functions and must be intialised
      this.ethernetSocket.onData = data => {
        try {
          if (data) {
            const printerBits: Int8Array = new Int8Array(data);
            if (printerBits) {
              const binarySet: number[][] = UtilityClass.convertIntArrayToBinaries(printerBits);
              if (printerBits.length > 1) {
                // FIRE A PING REQUEST FOR ETHERNET
                if (this.Active) {
                  if (this._watcherService) this._watcherService.broadcast(PRINTER_STATUS_PING, this);

                  // only if printer is active, try to restart
                  this._restartPingTimer();
                }
              } else if (printerBits.length == 1) {
                // if not expecting a response, do not process
                if (!this.DLE_EOT_waiting) {
                  this._checkStatusReset();
                  return;
                }

                // this is a DLE EOT call
                if (binarySet && binarySet.length > 0) {
                  if (binarySet[0][0] == 0 && binarySet[0][1] == 1 && binarySet[0][4] == 1 && binarySet[0][7] == 0) {
                    const statusCheckResult: IPrinterStatus = Printer.AnalysePrinterStatus(
                      binarySet[0],
                      this.DLE_EOT_fn,
                    );
                    if (!statusCheckResult.success) {
                      if (this.DLE_EOT_fn == 1) {
                        if (statusCheckResult.status == PRINTER_STATUS.OFFLINE) {
                          this.DLE_EOT_waiting = false;
                          this._sendStatusCheck(2);
                        } else {
                          this._printerStatusReady.next(statusCheckResult);
                        }
                      } else if (this.DLE_EOT_fn == 2) {
                        if (statusCheckResult.status == PRINTER_STATUS.ERROR_OCCURRED) {
                          this.DLE_EOT_waiting = false;
                          this._sendStatusCheck(3);
                        } else {
                          this._printerStatusReady.next(statusCheckResult);
                        }
                      } else if (this.DLE_EOT_fn == 3) {
                        this._printerStatusReady.next(statusCheckResult);
                      }
                    } else {
                      this._printerStatusReady.next(statusCheckResult);
                    }
                  }
                }
              }
            }
          }
        } catch (err) {
          console.log('Printer Status - ON DATA Error', err);
        }
      };

      this.ethernetSocket.onError = errorMessage => {
        // invoked after error occurs during connection
        console.log('socket.onError', errorMessage);
      };
      this.ethernetSocket.onClose = hasError => {
        // invoked after connection close
        console.log('socket.onClose', hasError);
      };

      // OPEN THE SOCKET
      if (
        this.ethernetSocket &&
        this.ethernetSocket.state != Socket.State.OPENED &&
        this.ethernetSocket != Socket.State.OPENING
      ) {
        this._openFromCreate()
          .then(
            success => {
              resolve(success);
            },
            fail => {
              resolve(false);
            },
          )
          .catch(() => {
            resolve(false);
          });
      }
    });
  }

  /**
   * Open the socket
   * @returns Promise of opening ethernet socket
   */
  private _openFromCreate(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.ethernetSocket.open(
        this.Address,
        this.Port,
        () => {
          console.log('SOCKET OPEN FOR ' + this.Address);
          resolve(true);
        },
        () => {
          console.log('SOCKET FAILED TO OPEN FOR ' + this.Address);
          reject(false);
        },
      );
    });
  }

  disconnectPrinter(hard: boolean = true) {
    if (this.ethernetSocket) {
      this.ethernetSocket.close();
      if (hard) this.ethernetSocket = null;
    }
  }

  public writeMessage = (encodedMessage): Promise<IPrintResponse> => {
    return new Promise(resolve => {
      this.isSocketOpen(true)
        .then(
          open => {
            if (open) {
              this.ethernetSocket.write(
                encodedMessage,
                resp => {
                  resolve({ success: true, message: '' });
                },
                err => {
                  resolve({ success: false, message: PRINTER_STATUS.SOCKET_CLOSED_1 });
                },
              );
            } else {
              resolve({ success: false, message: PRINTER_STATUS.SOCKET_CLOSED_2 });
            }
          },
          fail => {
            resolve({ success: false, message: PRINTER_STATUS.SOCKET_CLOSED_3 });
          },
        )
        .catch(err => {
          resolve({ success: false, message: PRINTER_STATUS.SOCKET_CLOSED_4 });
        });
    });
  };

  private async _sendStatusCheck(status: number) {
    if (!this.DLE_EOT_waiting) {
      clearTimeout(this.DLE_EOT_timeout);

      this.DLE_EOT_waiting = true;
      this.DLE_EOT_fn = status;

      const encoder = new EscPosEncoder();
      let result = encoder
        .initialize()
        .raw([0x10, 0x04, status]) // DLE EOT
        .encode();

      try {
        this.updateStatus({
          success: true,
          status: PRINTER_STATUS.CHECKING,
          statusFn: status,
        });

        const writeListen = await this.writeMessage(result);
        if (writeListen && !writeListen.success) {
          this._printerStatusReady.next({
            success: false,
            status: PRINTER_STATUS.SOCKET_CLOSED_5,
            statusFn: this.DLE_EOT_fn,
          });
        }
      } catch (err) {
        this._printerStatusReady.next({
          success: false,
          status: PRINTER_STATUS.UNKNOWN,
          statusFn: this.DLE_EOT_fn,
        });

        this._checkStatusReset();
      }
    }
  }

  /**
   * Check the status of the socket
   * @param autoConnect - Flagger to force the socket to connect if it is closed
   * @returns Promise to check the socket connection
   */
  async isSocketOpen(autoConnect: boolean = true): Promise<boolean> {
    return new Promise(async isSocketOpenResolve => {
      try {
        if (!this.ethernetSocket) {
          if (autoConnect) {
            this.restartSocket();
          } else {
            isSocketOpenResolve(false);
            return;
          }
        }

        switch (this.ethernetSocket.state) {
          case Socket.State.OPENED:
            console.log('OPENED... writing...');
            isSocketOpenResolve(true);
            break;
          case Socket.State.OPENING:
            console.log('OPENING... waiting...');

            // wait for 6 seconds
            const waitCheckInterval = interval(2000).subscribe(count => {
              if (this.ethernetSocket && this.ethernetSocket.state == Socket.State.OPENED) {
                waitCheckInterval && waitCheckInterval.unsubscribe();
                isSocketOpenResolve(true);
              } else {
                if (count == 2) {
                  // 3rd attempt
                  waitCheckInterval && waitCheckInterval.unsubscribe();
                  isSocketOpenResolve(false);
                }
              }
            });
            break;
          case Socket.State.CLOSED:
          case Socket.State.CLOSING:
          default:
            if (autoConnect) {
              console.log(`${this.ethernetSocket.state}... attempting to reopen.`);
              let result = await this._openFromCreate().catch(err => false);

              // if reconnecting failed
              // try to kill the connection and establish a new one
              if (!result) {
                console.log(`${this.ethernetSocket.state}... restarting socket.`);
                this.restartSocket();

                // do another check but don't force an auto connect
                result = await this.isSocketOpen(false);
              }

              console.log(`${this.ethernetSocket.state}... trying to open.`);
              isSocketOpenResolve(result);
            } else {
              console.log(`${this.ethernetSocket.state}...`);
              isSocketOpenResolve(false);
            }
        }
      } catch (e) {
        isSocketOpenResolve(false);
      }
    });
  }

  private _checkStatusReset() {
    this.DLE_EOT_fn = 1;
    this.DLE_EOT_waiting = false;
  }
}
